import type { IsoAndUnixTimestamp } from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import type { PayoutTargetBankAccountType } from "@@/shared/financials/payout-targets/payout-target-bank-account";
import {
    bankAccountNumberZodSchema,
    payoutTargetBankAccountTypeZodSchema,
} from "@@/shared/financials/payout-targets/payout-target-bank-account";
import { idZodSchema } from "@@/shared/id";
import type { ProviderId } from "@@/shared/providers/provider-id";
import { providerIdZodSchema } from "@@/shared/providers/provider-id";
import { generateId } from "@@/utilities/generate-id";
import type { Except } from "type-fest";
import { z } from "zod";
import type { PayoutTargetBankgiroType } from "../payout-target-bankgiro";
import {
    bankGiroZodSchema,
    payoutTargetBankgiroTypeZodSchema,
} from "../payout-target-bankgiro";
import type { PayoutTargetPlusgiroType } from "../payout-target-plusgiro";
import {
    payoutTargetPlusgiroTypeZodSchema,
    plusGiroZodSchema,
} from "../payout-target-plusgiro";

type BankgiroTarget = { type: PayoutTargetBankgiroType; bankgiro: string };
type PlusGiroTarget = { type: PayoutTargetPlusgiroType; plusgiro: string };
type BankAccountTarget = {
    type: PayoutTargetBankAccountType;
    bankAccountNumber: string;
};

type PayoutTarget = BankgiroTarget | BankAccountTarget | PlusGiroTarget;

type CreatePayoutTargetCommand = {
    readonly _id: string;
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly _type: "CREATE_PAYOUT_TARGET_COMMAND";

    readonly providerId: ProviderId;

    readonly target: PayoutTarget;
};

const payoutTargetBankgiroZodSchema: z.ZodSchema<BankgiroTarget> = z.object({
    type: payoutTargetBankgiroTypeZodSchema,
    bankgiro: bankGiroZodSchema,
});
const payoutTargetPlusGiroZodSchema: z.ZodSchema<PlusGiroTarget> = z.object({
    type: payoutTargetPlusgiroTypeZodSchema,
    plusgiro: plusGiroZodSchema,
});
const payoutTargetBankAccountZodSchema: z.ZodSchema<BankAccountTarget> =
    z.object({
        type: payoutTargetBankAccountTypeZodSchema,
        bankAccountNumber: bankAccountNumberZodSchema,
    });

const PayoutTarget = z.union([
    payoutTargetBankgiroZodSchema,
    payoutTargetPlusGiroZodSchema,
    payoutTargetBankAccountZodSchema,
]);

type FactoryParams = Except<
    CreatePayoutTargetCommand,
    "_id" | "_type" | "_createdAt"
>;
const createPayoutTargetCommandFactory = (params: FactoryParams) => {
    const CreatePayoutTargetCommand: CreatePayoutTargetCommand = {
        ...params,
        _id: generateId({ prefix: "note_command__" }),
        _type: "CREATE_PAYOUT_TARGET_COMMAND",
        _createdAt: nowIsoAndUnix(),
    };
    return CreatePayoutTargetCommand;
};

const createPayoutTargetCommandZodSchema: z.ZodSchema<CreatePayoutTargetCommand> =
    z.object({
        _id: idZodSchema,
        _createdAt: isoAndUnixZodSchema,
        _type: z.literal("CREATE_PAYOUT_TARGET_COMMAND"),
        providerId: providerIdZodSchema,
        target: PayoutTarget,
    });

export { createPayoutTargetCommandFactory, createPayoutTargetCommandZodSchema };
export type { CreatePayoutTargetCommand };
